import { mountLoadingSpinner } from '../../components/LoadingSpinner';
import { RELATED_LINKS_CONTAINER_ID } from 'Container/App/constants';
import getCustomerConfig from 'Container/App/helpers/getCustomerConfig';
import getAvailableSubscriptionIds from 'Container/App/helpers/getAvailableSubscriptionIds';
import mountTariffOptionPicker from '../../components/TariffOptionPicker';
import mountGigakombiOptionPicker from '../../components/GigakombiOptionPicker';
import mountRestlaufzeitOptionPicker from '../../components/RlzOptionPicker';
import mountSalesChannelOptionPicker from '../../components/SalesChannelOptionPicker';
import {
    mountAppBeltContainer,
    mountDeviceFirstBeltContainer,
    mountErrorBeltContainer,
} from '../../components/BeltContainer';
import { mountOfferSummaryCard } from '../../components/OfferSummaryCard';
import { mountHorizontalList } from '../../components/HorizontalList';
import { restoreFocusAfterRedirect } from 'Container/App/helpers/restoreFocusAfterRedirect';
import {
    mountGigaDepotIconText,
    mountRestlaufzeitIconText,
    mountTauschbonusIconText,
    mountTradeInIconText,
} from '../../components/IconText';
import mountRestlaufzeitHeadline from '../../components/Headline';
import mountImageHeader from '../../components/ImageHeader';
import mountItemSummaryCard from '../../components/DeviceCard';
import { mountDeviceNotification } from '../../components/Notification';
import { mountAccordions } from '../../components/Accordion';
import { prefetchRedirectionPages } from 'Container/App/helpers/prefetchRedirectionPages';
import mountOverlay from '../../components/Overlay';
import { mountGigaDepotButtonLink } from '../../components/ButtonLink';
import getPreviousHardwareConfig from 'Container/App/helpers/getPreviousHardwareConfig';
import { useDispatch } from '../../store';
import {
    setGigakombi,
    setInitialState,
    setSalesChannel,
} from 'Container/App/slice';
import { initEditorialOverlays } from '@vfde-sails/overlay';
import {
    createComponentById,
    NO_PATTERN_BUSINESS_LOGIC,
} from '@vfde-brix/ws10/core';
import { createRelatedLinks } from '@vfde-brix/ws10/related-links';
import {
    mountCashbackPromotionalCard,
    mountGigaDepotPromotionalCard,
} from '../../components/PromotionalCard';
import { startListeners } from './listener';

const mountTariffOverviewAppContainer = () => {
    startListeners();

    const dispatch = useDispatch();

    // Layout elements
    mountLoadingSpinner();
    mountAppBeltContainer();
    mountErrorBeltContainer();
    mountDeviceFirstBeltContainer();
    mountImageHeader();

    // Option pickers
    const tariffOptionPicker = mountTariffOptionPicker();
    const gigakombiPicker = mountGigakombiOptionPicker();
    mountRestlaufzeitOptionPicker();
    const salesChannelPicker = mountSalesChannelOptionPicker();

    // Offer price
    mountOfferSummaryCard();

    // Informative elements
    mountHorizontalList();
    mountRestlaufzeitHeadline();
    mountRestlaufzeitIconText();
    mountTradeInIconText();
    mountTauschbonusIconText();
    mountGigaDepotIconText();
    mountItemSummaryCard();
    mountDeviceNotification();
    mountGigaDepotPromotionalCard();
    mountCashbackPromotionalCard();

    // Has nothing to do with GigaDepot, doesn't need to be toggled when GigaDepot is toggled, but the class in CMS is still named that way.
    mountGigaDepotButtonLink();

    // Just mount to enable functionality
    createComponentById(createRelatedLinks, RELATED_LINKS_CONTAINER_ID, NO_PATTERN_BUSINESS_LOGIC);
    mountAccordions();

    // Overlays
    mountOverlay();

    // Init editorial overlays
    initEditorialOverlays(dispatch);

    // Get data for initial state
    const availableSubscriptions = getAvailableSubscriptionIds(tariffOptionPicker);
    const customerConfig = getCustomerConfig(availableSubscriptions, tariffOptionPicker);
    const previousHardware = !customerConfig.atomicId ? getPreviousHardwareConfig() : null;

    dispatch(setInitialState(
        {
            availableSubscriptionIds: availableSubscriptions,
            customerConfig,
            previousHardwareConfig: previousHardware,
        },
    ));

    // Restore focus after switching gigakombi/sales channel via redirect, so the user can continue smoothly in their tab flow
    restoreFocusAfterRedirect({
        [setSalesChannel.type]: salesChannelPicker,
        [setGigakombi.type]: gigakombiPicker,
    });

    // Prefetch redirection content to speed up user interaction
    prefetchRedirectionPages(customerConfig);

    window.addEventListener('pageshow', event => {
        if (event.persisted) {
            // Read initial state from storage
            dispatch(setInitialState({
                availableSubscriptionIds: getAvailableSubscriptionIds(tariffOptionPicker),
                customerConfig: getCustomerConfig(availableSubscriptions, tariffOptionPicker),
                previousHardwareConfig: getPreviousHardwareConfig(),
            }));
        }
    });
};

export default mountTariffOverviewAppContainer;
